import { Workspace } from '@/features/iam/workspaces/Workspace'
import { omit } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { combineLatest, EMPTY, merge, type Observable } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeWhile,
  withLatestFrom,
} from 'rxjs/operators'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type CursorPageQuery } from '@/infra/paging'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { fromStream, toStream, tapAction } from '@/utils/mobx-util'
import { deepEqual } from '@/utils/object-util'
import { shareLatest, unpair, withAbortSignal } from '@/utils/rx-util'
import { type Opportunity } from './Opportunity'
import { type OpportunityEvent } from './opportunity-events'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import {
  type ListOpportunitiesFn,
  type ListOpportunitiesQuery,
  type OpportunitySortingField,
} from './OpportunityStore'

/**
 * Implements opportunity searching.
 */
export class OpportunitiesSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Backing field for the last search.
   */
  @observable.ref private accessor _lastSearch: OpportunitySearchResult = {
    opportunities: [],
    searchFilter: OpportunitySearchFilter.none,
    total: 0,
    filtersApplied: false,
  }

  /**
   * Workspace filter.
   */
  @observable accessor workspaceFilter: OpportunityWorkspaceFilter = {
    type: 'AllWorkspaces',
  }

  /**
   * Sorting.
   */
  @observable accessor sorting: OpportunitySort = { type: 'Default' }

  /**
   * Search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Stream for workspace filter values. Emits on subscribe.
   */
  readonly workspaceFilter$ = toStream(() => this.workspaceFilter, true)

  /**
   * Opportunities scroller.
   */
  readonly scroller = new InfiniteScrollProvider<
    Opportunity,
    OpportunitySearchFilter
  >({
    limit: 20,
    fetchItems: ({ paging, filter, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Unable to load opportunities',
        signal,
      )

      return withRetry(() =>
        this._opportunityFetcher(
          toListOpportunitiesQuery(paging, filter),
          signal,
        ),
      )
    },
  })

  /**
   * Search filter changes.
   */
  private readonly _searchFilter$ = combineLatest([
    // Combine the search text changes...
    this.searchText.debouncedValue$,
    // ...with the workspace filter changes
    toStream(() => this.workspaceFilter, true),
    // ...and the sort
    toStream(() => this.sorting, true),
  ]).pipe(
    // Map what we've combined to a Search Filter.
    map(([searchText, workspaceFilter, sort]): OpportunitySearchFilter => ({
      searchText,
      sort,
      workspace:
        workspaceFilter.type === 'AllWorkspaces'
          ? Workspace.none
          : workspaceFilter.workspace,
    })),
    // Make sure to only subscribe once, share the latest value.
    shareLatest(),
  )

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  readonly searches$ = this._searchFilter$.pipe(
    // Start a non-filtered search immediately.
    startWith(OpportunitySearchFilter.none),
    // Ignore emissions with the same search filter.
    distinctUntilChanged<OpportunitySearchFilter>(
      OpportunitySearchFilter.equals,
    ),
    // Start a search, return the filter that was used.
    switchMap((query): Observable<OpportunitySearchResult> =>
      withAbortSignal(async (signal) => {
        let result = await this.scroller.fetch({ filter: query, signal })
        // Keep loading more until we get something back that isn't being filtered out
        // on the client. If we don't do this here, we potentially display an empty state
        // which does not trigger infinite scroll.
        while (this.opportunities.length === 0 && !this.scroller.isDone) {
          signal.throwIfAborted()
          result = await this.scroller.more(signal)
        }
        return {
          searchFilter: query,
          filtersApplied: !OpportunitySearchFilter.isNone(query),
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- A zero total falls back to the loaded count too.
          total: result.total || this.opportunities.length,
          opportunities: this.opportunities,
        }
      }).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      ),
    ),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  constructor(
    private readonly _opportunityFetcher: ListOpportunitiesFn,
    events$: Observable<OpportunityEvent>,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    // Only react to opportunity insertions/updates if they match our workspace filter,
    // and only if we are not doing a text search.
    // This means that we would potentially miss events if searching by text, but that's
    // not really something most people do for opportunities.
    const filteredEvents$ = events$.pipe(
      withLatestFrom(this.searches$),
      filter(([event, search]) => {
        // Always handle opportunity removed events.
        if (event.type === 'OpportunityRemoved') {
          return true
        }

        // If we are not filtering at all, handle the event.
        if (OpportunitySearchFilter.isNone(search.searchFilter)) {
          return true
        }

        // If there's search text, don't handle the event.
        if (search.searchFilter.searchText) {
          return false
        }

        // If the workspace of the opportunity matches that of the filter, handle the event.
        return search.searchFilter.workspace === event.opportunity.workspace
      }),
      // We only care about the event, not the search.
      unpair(),
    )

    this._lifecycle.manage(
      merge(
        // Can't use fromStream because it would subscribe immediately,
        // which we don't want.
        this.searches$.pipe(tapAction((search) => (this._lastSearch = search))),
        syncInfiniteScrollerItems({
          // SAFETY: syncing only adds and removes items, which never touches
          // the filter type `syncInfiniteScrollerItems` doesn't carry.
          scroller: this
            .scroller as unknown as InfiniteScrollProvider<Opportunity>,
          additions$: filteredEvents$.pipe(
            filter((e) => e.type !== 'OpportunityRemoved'),
            map((e) => e.opportunity),
          ),
          removals$: filteredEvents$.pipe(
            filter((e) => e.type === 'OpportunityRemoved'),
            map((e) => e.opportunity),
          ),
        }),
      ),
    )
  }

  /**
   * The internal opportunities list.
   */
  @computed
  get opportunities() {
    return this.scroller.items.slice()
  }

  /**
   * The workspace being filtered by, or `Workspace.none` for all workspaces.
   */
  @computed
  get selectedWorkspace() {
    return this.workspaceFilter.type === 'AllWorkspaces'
      ? Workspace.none
      : this.workspaceFilter.workspace
  }

  /**
   * Gets the current search state.
   */
  @computed
  get searchState(): SearchState {
    if (this._getIsFirstFetchRunning()) {
      return 'InitialSearchRunning'
    }

    if (this._lastSearch.opportunities.length === 0 && this.scroller.isDone) {
      return this._lastSearch.filtersApplied
        ? 'EmptyButFilterApplied'
        : 'CompletelyEmpty'
    }

    return 'Some'
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'SpecificWorkspace' }
  }

  /**
   * Clears the filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.selectWorkspaceForFiltering(Workspace.none)
  }

  /**
   * Subscribes to the pending state of the fetch task.
   * Once it flips to `false`, stop.
   */
  private readonly _getIsFirstFetchRunning = fromStream(
    true,
    toStream(() => this.scroller.fetch.pending, true).pipe(
      takeWhile(Boolean, true),
    ),
  )
}

/**
 * Creates a `ListOpportunities` from the paging and filter.
 *
 * @param paging
 * @param filter
 */
function toListOpportunitiesQuery(
  paging: CursorPageQuery,
  filter: OpportunitySearchFilter | undefined,
): ListOpportunitiesQuery {
  const orderBy: ListOpportunitiesQuery['orderBy'] =
    filter?.sort.type === 'Field'
      ? {
          [filter.sort.field]:
            filter.sort.direction === 'Descending' ? 'desc' : 'asc',
        }
      : // Sort by last modified
        {
          Modified: 'desc',
        }

  return {
    orderBy,
    searchText: filter?.searchText,
    workspaceId: filter?.workspace.id || undefined,
    limit: paging.limit,
    after: paging.after,
  }
}

/**
 * Workspace filter for Opportunities.
 */
export type OpportunityWorkspaceFilter = AllWorkspaces | SpecificWorkspace

/**
 * Opportunities in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * Opportunities in a specific workspace.
 */
export interface SpecificWorkspace {
  type: 'SpecificWorkspace'
  workspace: Workspace
}

/**
 * Opportunity sorting type.
 */
export type OpportunitySort = DefaultSort | FieldSort

/**
 * Sort direction.
 */
export type SortDirection = 'Ascending' | 'Descending'

/**
 * Default sort.
 */
export interface DefaultSort {
  type: 'Default'
}

/**
 * Field sort.
 */
export interface FieldSort {
  type: 'Field'
  field: OpportunitySortingField
  direction: SortDirection
}

/**
 * Opportunity search filter.
 */
export interface OpportunitySearchFilter {
  searchText?: string
  workspace: Workspace
  sort: OpportunitySort
}

export const OpportunitySearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    searchText: '',
    workspace: Workspace.none,
    sort: { type: 'Default' },
  } as OpportunitySearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: OpportunitySearchFilter) =>
    OpportunitySearchFilter.equals(
      omit(OpportunitySearchFilter.none, 'sort'),
      omit(searchFilter, 'sort'),
    ),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (
    left: Partial<OpportunitySearchFilter>,
    right: Partial<OpportunitySearchFilter>,
  ) => deepEqual(left, right),
}

/**
 * Search result.
 */
export interface OpportunitySearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: OpportunitySearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The total number of search results (not just what was returned).
   */
  total: number
  /**
   * The current list of opportunities contained in the searcher.
   */
  readonly opportunities: readonly Opportunity[]
}

/**
 * Current state of searching.
 */
export type SearchState =
  /**
   * The initial search is currently running.
   */
  | 'InitialSearchRunning'
  /**
   * The search returned something.
   */
  | 'Some'
  /**
   * The search returned an empty result, but
   * there was a filter applied.
   */
  | 'EmptyButFilterApplied'
  /**
   * The search returned an empty result
   * and no filters were applied.
   */
  | 'CompletelyEmpty'
